import { create } from "zustand";
import { getProductTour, patchProductTour } from "@/api/client";
import { useAuthStore } from "@/stores/auth";
import { ApiError } from "@/lib/apiError";
import { applyTourPatch, emptyTourState, normalizeTourState, PRODUCT_TOUR_VERSION, TOUR_CHAPTERS, type ProductTourPatch, type ProductTourState } from "@/lib/productTour";

interface PendingPatch { id: string; patch: ProductTourPatch }
interface TourStore {
  ownerId: string | null;
  state: ProductTourState;
  pending: PendingPatch[];
  ready: boolean;
  saving: boolean;
  syncError: boolean;
  syncNotice: string | null;
  initialize: (userId: string | null) => Promise<void>;
  update: (patch: ProductTourPatch) => void;
  retry: () => Promise<void>;
}
const keyFor = (id: string) => `trace:product-tour:v${PRODUCT_TOUR_VERSION}:${id}`;
let generation = 0;
let draining: number | null = null;
let reading: number | null = null;
let mutationEpoch = 0;

function cached(id: string): { state: ProductTourState; pending: PendingPatch[] } {
  try {
    const data = JSON.parse(localStorage.getItem(keyFor(id)) ?? "null");
    const ids = new Set(TOUR_CHAPTERS.map((chapter) => chapter.id));
    if (data?.state?.version !== PRODUCT_TOUR_VERSION || !data.state.chapters || !Array.isArray(data.pending)) throw new Error("invalid cache");
    const state = normalizeTourState(data.state);
    const pending = data.pending.filter((item: PendingPatch) => typeof item.id === "string" && item.patch && (!item.patch.chapter_id || ids.has(item.patch.chapter_id)) && (!("current_step" in item.patch) || (Number.isInteger(item.patch.current_step) && item.patch.current_step! >= 0 && item.patch.current_step! <= 15 && item.patch.current_chapter !== null)));
    return { state, pending };
  } catch {
    // Offline fallback retains a deliberate whole-tour skip, but never
    // imports old exercise completion or pending demo writes into v3.
    try {
      const legacy = JSON.parse(localStorage.getItem(`trace:product-tour:v2:${id}`) ?? "null");
      if (legacy?.state?.version === 2 && legacy.state.status === "skipped") return { state: { ...emptyTourState(), status: "skipped" }, pending: [] };
    } catch { /* Invalid legacy browser data is ignored. */ }
    return { state: emptyTourState(), pending: [] };
  }
}
function persist(store: Pick<TourStore, "ownerId" | "state" | "pending">) {
  if (!store.ownerId) return;
  try { localStorage.setItem(keyFor(store.ownerId), JSON.stringify({ state: store.state, pending: store.pending })); } catch { /* The server still saves progress if browser storage is unavailable. */ }
}

async function drain() {
  const run = generation;
  if (draining === run) return;
  draining = run;
  const store = useProductTourStore;
  store.setState({ saving: true });
  try {
    while (run === generation) {
      const first = store.getState().pending[0];
      if (!first) break;
      try {
        const ownerId = store.getState().ownerId;
        if (!ownerId || useAuthStore.getState().user?.id !== ownerId) return;
        const server = await patchProductTour(ownerId, first.patch);
        if (run !== generation) return;
        const remaining = store.getState().pending.filter((item) => item.id !== first.id);
        const state = remaining.reduce((value, item) => applyTourPatch(value, item.patch), normalizeTourState(server));
        store.setState({ state, pending: remaining, syncError: false });
        persist(store.getState());
      } catch (error) {
        if (run !== generation) return;
        if (error instanceof ApiError && [403, 409, 422].includes(error.status)) {
          // Permissions or catalog validation can change while offline. A
          // rejected update must not block every later valid chapter forever.
          const remaining = store.getState().pending.filter((item) => item.id !== first.id);
          store.setState({ pending: remaining, syncNotice: "A progress update was rejected. Account progress has been refreshed." });
          persist(store.getState());
          try {
            const ownerId = store.getState().ownerId;
            if (!ownerId) return;
            const server = await getProductTour(ownerId);
            if (run !== generation) return;
            store.setState({ state: store.getState().pending.reduce((value, item) => applyTourPatch(value, item.patch), normalizeTourState(server)), syncError: false });
            persist(store.getState());
          } catch { if (run === generation) store.setState({ syncError: true }); break; }
          continue;
        }
        store.setState({ syncError: true });
        break;
      }
    }
  } finally {
    if (run === generation) store.setState({ saving: false });
    if (draining === run) draining = null;
  }
}

export const useProductTourStore = create<TourStore>((set, get) => ({
  ownerId: null, state: emptyTourState(), pending: [], ready: false, saving: false, syncError: false, syncNotice: null,
  initialize: async (userId) => {
    if (get().ownerId === userId) return;
    const run = ++generation;
    set({ ownerId: userId, ...(userId ? cached(userId) : { state: emptyTourState(), pending: [] }), ready: false, saving: false, syncError: false, syncNotice: null });
    if (!userId) return;
    reading = run;
    try {
      const server = await getProductTour(userId);
      if (run !== generation) return;
      const state = get().pending.reduce((value, item) => applyTourPatch(value, item.patch), normalizeTourState(server));
      set({ state, ready: true });
      persist(get());
      if (get().pending.length) void drain();
    } catch {
      if (run === generation) set({ ready: true, syncError: true });
    } finally { if (reading === run) reading = null; }
  },
  update: (patch) => {
    if (!get().ownerId || useAuthStore.getState().user?.id !== get().ownerId) return;
    ++mutationEpoch;
    const pending = [...get().pending, { id: crypto.randomUUID(), patch }];
    set({ state: applyTourPatch(get().state, patch), pending });
    persist(get());
    if (get().ready) void drain();
  },
  retry: async () => {
    const ownerId = get().ownerId;
    if (!ownerId || !get().ready || useAuthStore.getState().user?.id !== ownerId) return;
    if (get().pending.length) { await drain(); return; }
    const run = generation;
    if (reading === run) return;
    reading = run;
    const readEpoch = mutationEpoch;
    try {
      const server = await getProductTour(ownerId);
      if (run !== generation) return;
      // A PATCH may have finished after this GET started. Its newer snapshot
      // must survive even when the pending queue is now empty.
      if (readEpoch !== mutationEpoch) return;
      set({ state: get().pending.reduce((value, item) => applyTourPatch(value, item.patch), normalizeTourState(server)), ready: true, syncError: false });
      persist(get());
      if (get().pending.length) await drain();
    } catch { if (run === generation) set({ syncError: true }); }
    finally { if (reading === run) reading = null; }
  },
}));

// Reset synchronously on logout/account switch, before a React effect runs.
// In-flight responses are generation-guarded and requests bind their account.
useAuthStore.subscribe((next, previous) => {
  const nextId = next.user?.id ?? null;
  if (nextId !== (previous.user?.id ?? null)) void useProductTourStore.getState().initialize(nextId);
});
